import { useTranslation } from 'react-i18next';
import {
    useTheme,
    Typography,
    Box,
    Stack,
    Icon,
    PieChart,
    Pie,
    Cell,
} from '../../../../base';
import {
    StyledCard,
    StyledDonutWrapper,
    StyledCenterLabel,
    StyledTrendRow,
} from './MetricDonutCard.styles';

interface IMetricDonutCardProps {
    title: string;
    subtitle: string;
    percentage: number;
    averageDays: number | null;
    countPercentage: number;
    footerText?: string;
}

const MetricDonutCard = ({
    title,
    subtitle,
    percentage,
    averageDays,
    countPercentage,
    footerText,
}: IMetricDonutCardProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const clampedPercentage = Math.min(Math.max(percentage, 0), 100);
    const filledValue = clampedPercentage;
    const emptyValue = 100 - clampedPercentage;

    const donutData = [
        { name: 'filled', value: filledValue },
        { name: 'empty', value: emptyValue },
    ];

    const COLORS = [theme.palette.warning.main, theme.palette.grey[200]];

    return (
        <StyledCard>
            <StyledDonutWrapper>
                <PieChart width={120} height={120}>
                    <Pie
                        data={donutData}
                        innerRadius={45}
                        outerRadius={55}
                        startAngle={90}
                        endAngle={-270}
                    >
                        {donutData.map((entry, index) => (
                            <Cell
                                key={`cell-${entry.name}`}
                                fill={COLORS[index]}
                            />
                        ))}
                    </Pie>
                </PieChart>
                <StyledCenterLabel>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {Math.round(clampedPercentage)}%
                    </Typography>
                </StyledCenterLabel>
            </StyledDonutWrapper>

            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color={theme.palette.grey[500]}>
                    {subtitle}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: theme.spacing(1),
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {averageDays !== null
                            ? `${Math.round(averageDays)} ${t('reportsPage.timeToHireReport.days')}`
                            : t('reportsPage.timeToHireReport.noData')}
                    </Typography>
                    <StyledTrendRow>
                        <Typography
                            variant="caption"
                            color={theme.palette.success.main}
                            sx={{ fontWeight: 600 }}
                        >
                            {Math.round(countPercentage)}%
                        </Typography>
                        <Icon
                            name="MovingIcon"
                            size={14}
                            style={{ color: theme.palette.success.main }}
                        />
                    </StyledTrendRow>
                </Box>

                {footerText && (
                    <Typography
                        variant="caption"
                        color={theme.palette.grey[400]}
                        sx={{ fontSize: 10 }}
                    >
                        {footerText}
                    </Typography>
                )}
            </Stack>
        </StyledCard>
    );
};

export default MetricDonutCard;
